import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, ShoppingBag, Menu, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { NAV_LINKS } from "@/data/content";
import { useCart } from "@/context/CartContext";
import { useSiteAssets } from "@/hooks/useSiteAssets";
import { cn } from "@/lib/utils";

export const HEADER_HEIGHT = 76;

// Navbar: si compatta e diventa translucida (backdrop blur) dopo 80px di scroll.
export function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { count, openDrawer } = useCart();
  const { get: getAsset } = useSiteAssets();
  const siteLogo = getAsset("logo");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/catalogo?search=${encodeURIComponent(q.trim())}`);
      setQ("");
      setOpen(false);
    }
  };

  const isActive = (to) => location.pathname === to || (to !== "/" && location.pathname.startsWith(to + "/"));

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color,backdrop-filter] duration-300 ease-out-expo",
        scrolled
          ? "border-line bg-ink/80 backdrop-blur-xl supports-[backdrop-filter]:bg-ink/75"
          : "border-transparent bg-transparent"
      )}
      data-testid="site-header"
      data-scrolled={scrolled ? "true" : "false"}
    >
      <div
        className={cn(
          "container-page flex items-center gap-6 transition-[height] duration-300 ease-out-expo",
          scrolled ? "h-14" : "h-[76px]"
        )}
      >
        {/* Mobile menu */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild className="md:hidden">
            <Button variant="ghost" size="icon" data-testid="header-mobile-menu-button" aria-label="Menu" className="-ml-2">
              <Menu className="h-5 w-5" strokeWidth={1.5} />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-screen max-w-none border-line bg-ink p-0">
            <div className="flex h-full flex-col">
              <SheetHeader className="border-b border-line px-6 py-5 text-left">
                <SheetTitle className="font-display text-lg font-bold tracking-tightest">
                  RINOVA<span className="ml-1.5 inline-block h-1.5 w-1.5 -translate-y-2 rounded-[1px] bg-lime" />
                </SheetTitle>
                <SheetDescription className="sr-only">Menu di navigazione principale</SheetDescription>
              </SheetHeader>
              <div className="px-6 pt-6">
                <form onSubmit={submitSearch}>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
                    <Input
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Cerca un prodotto"
                      className="rounded-full pl-11"
                      data-testid="header-search-input-mobile"
                    />
                  </div>
                </form>
              </div>
              <nav className="mt-4 flex flex-col px-3">
                {NAV_LINKS.map((l, i) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className={cn(
                      "group flex items-center justify-between border-b border-line px-3 py-5 font-display text-2xl font-semibold tracking-tightest transition-colors",
                      isActive(l.to) ? "text-lime" : "text-foreground hover:text-lime"
                    )}
                    data-testid={`mobile-nav-link-${i}`}
                  >
                    <span>{l.label}</span>
                    <ArrowUpRight className="h-5 w-5 text-muted-foreground transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.5} />
                  </Link>
                ))}
              </nav>
              <div className="mt-auto px-6 pb-8 pt-6">
                <div className="mono-label text-muted-foreground">Garanzia 12 mesi · Reso 14 giorni</div>
              </div>
            </div>
          </SheetContent>
        </Sheet>

        {/* Wordmark */}
        <Link
          to="/"
          className="flex items-center font-display text-xl font-bold tracking-tightest text-foreground"
          aria-label="RINOVA home"
          data-testid="header-logo"
        >
          {siteLogo ? (
            <img src={siteLogo.url} alt={siteLogo.alt || "RINOVA"} className="h-7 w-auto object-contain" />
          ) : (
            <>
              RINOVA
              <span className="ml-1.5 inline-block h-1.5 w-1.5 -translate-y-2 rounded-[1px] bg-lime" aria-hidden="true" />
            </>
          )}
        </Link>

        {/* Nav desktop */}
        <nav className="ml-6 hidden items-center gap-1 md:flex" aria-label="Navigazione principale">
          {NAV_LINKS.map((l) => {
            const active = isActive(l.to);
            return (
              <Link
                key={l.to}
                to={l.to}
                className={cn(
                  "relative rounded-full px-3.5 py-2 text-[13.5px] font-medium transition-colors duration-200",
                  active ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                )}
                aria-current={active ? "page" : undefined}
              >
                {l.label}
                <span
                  className={cn(
                    "absolute -bottom-0.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-[1px] bg-lime transition-[opacity,transform] duration-200",
                    active ? "opacity-100 scale-100" : "opacity-0 scale-50"
                  )}
                  aria-hidden="true"
                />
              </Link>
            );
          })}
        </nav>

        {/* Search desktop */}
        <form onSubmit={submitSearch} className="ml-auto hidden w-full max-w-[260px] md:flex" role="search">
          <div className="relative w-full">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca un prodotto"
              className={cn("rounded-full bg-surface/60 pl-11 transition-[height,border-color,box-shadow] duration-300", scrolled ? "h-9" : "h-10")}
              data-testid="header-search-input"
            />
          </div>
        </form>

        {/* Cart */}
        <Button
          variant="ghost"
          size="icon"
          className="relative ml-auto rounded-full md:ml-0"
          onClick={openDrawer}
          data-testid="header-cart-button"
          aria-label={`Carrello, ${count} articoli`}
        >
          <ShoppingBag className="h-5 w-5" strokeWidth={1.5} />
          {count > 0 && (
            <span
              className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-lime px-1 font-mono text-[10px] font-semibold text-ink"
              data-testid="header-cart-count"
            >
              {count}
            </span>
          )}
        </Button>
      </div>
    </header>
  );
}
